import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { scrollToSection as scrollTo } from "@/lib/scroll";
import { Menu, X, Truck, LayoutDashboard } from "lucide-react";

const dashPath = (role) => (role === "admin" ? "/admin" : role === "partner" ? "/partner/dashboard" : "/dashboard");

export default function Header() {
  const { lang, toggle, t } = useLang();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "book", label: t("nav.book") },
    { id: "services", label: t("nav.services") },
    { id: "process", label: t("nav.process") },
    { id: "drivers", label: t("nav.drivers") },
    { id: "contact", label: t("nav.contact") },
  ];

  const go = (id) => { scrollTo(id); setOpen(false); };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 transition-shadow duration-300 ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[74px] flex items-center justify-between">
        <button onClick={() => scrollTo("top")} className="flex items-center gap-2.5" data-testid="logo-button">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-ksy-navy text-white">
            <Truck className="w-5 h-5" />
          </span>
          <span className="leading-tight text-left">
            <span className="block font-display font-extrabold text-lg text-ksy-navy tracking-tight">KS&amp;Y Transport</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-ksy-slate">Auto Logistics · LLC</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-sm font-semibold text-slate-600 hover:text-ksy-navy transition-colors"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-ksy-navy border border-slate-300 rounded-md px-2.5 py-1.5 transition-colors"
            data-testid="language-toggle"
          >
            {lang === "en" ? "ES" : "EN"}
          </button>
          {user ? (
            <Link
              to={dashPath(user.role)}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-ksy-navy border border-slate-300 rounded-lg px-4 py-2.5 hover:border-ksy-blue hover:text-ksy-blue transition-colors"
              data-testid="header-dashboard-link"
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              className="hidden sm:inline-flex items-center text-sm font-bold text-ksy-navy hover:text-ksy-blue transition-colors px-2"
              data-testid="header-signin-link"
            >
              Sign in
            </Link>
          )}
          <button
            onClick={() => scrollTo("book")}
            className="hidden sm:inline-flex items-center bg-ksy-blue text-white text-sm font-bold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            data-testid="header-quote-cta"
          >
            {t("hero.cta")}
          </button>
          <button className="lg:hidden text-ksy-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="px-5 py-5 flex flex-col gap-4">
              {links.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} className="text-left text-base font-semibold text-slate-700" data-testid={`mobile-nav-${l.id}`}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("book")} className="mt-2 bg-ksy-blue text-white font-bold px-5 py-3 rounded-lg" data-testid="mobile-quote-cta">
                {t("hero.cta")}
              </button>
              {user ? (
                <Link to={dashPath(user.role)} onClick={() => setOpen(false)} className="text-left font-bold text-ksy-navy" data-testid="mobile-dashboard-link">Dashboard</Link>
              ) : (
                <div className="flex gap-3">
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center font-bold text-ksy-navy border border-slate-300 rounded-lg py-2.5" data-testid="mobile-signin-link">Sign in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center font-bold text-ksy-blue border border-blue-200 rounded-lg py-2.5" data-testid="mobile-register-link">Register</Link>
                </div>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
